import React, { useState } from 'react'
import Icon from '@components/atoms/Icon'
import { InputData } from './SizeGuide.tsx'

type SizeGuideTableProps = {
  columns?: string[]
  category?: string
  apparelFit?: string
  data: InputData[]
  isEyewear?: boolean
}

const SizeGuideTable: React.FC<SizeGuideTableProps> = ({ category, columns, apparelFit, data, isEyewear }) => {
  const [showMetric, setShowMetric] = useState(false)
  if (isEyewear) {
    return (
      <div>
        <div className="flex flex-wrap gap-6 px-2 py-6 lg:px-6 lg:gap-0">
          <h3 className="w-full font-appleGaramond text-24 leading-33 font-normal lg:w-10/12">{category}</h3>
          <div className="w-full flex gap-6 lg:gap-0 lg:grid lg:grid-cols-2 lg:w-2/12">
            <button onClick={() => setShowMetric(false)} className="flex items-center">
              <span className={showMetric ? 'hidden' : 'block'}>
                <Icon name="icon-radio-toggle-on" />
              </span>
              <span className={showMetric ? 'block' : 'hidden'}>
                <Icon name="icon-radio-toggle-off" />
              </span>
              IN.
            </button>
            <button onClick={() => setShowMetric(true)} className="flex items-center">
              <span className={showMetric ? 'block' : 'hidden'}>
                <Icon name="icon-radio-toggle-on" />
              </span>
              <span className={showMetric ? 'hidden' : 'block'}>
                <Icon name="icon-radio-toggle-off" />
              </span>
              MM.
            </button>
          </div>
        </div>

        <table className="min-w-full text-left">
          <thead>
            <tr className="border-y border-grey-10">
              <th className="px-2 py-4 body-sm font-semibold lg:px-6">Size</th>
              <th className="px-2 py-4 body-sm font-semibold lg:px-6">Frame Width</th>
              <th className="px-2 py-4 body-sm font-semibold lg:px-6">Frame Height</th>
              <th className="px-2 py-4 body-sm font-semibold lg:px-6">Bridge Width</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index} className="border-y border-grey-10">
                <td className="px-2 py-7 body-sm font-semibold lg:px-6">{item.size}</td>
                <td className="px-2 py-7 body-sm lg:px-6 text-center">
                  {!showMetric
                    ? item.frame_width?.measurement
                      ? (parseFloat(item.frame_width.measurement.replace(/[^\d.]/g, '')) / 25.4).toFixed(2) + '"' // Convert to in
                      : 'N/A'
                    : item.frame_width?.measurement || 'N/A'}
                </td>
                <td className="px-2 py-7 body-sm lg:px-6 text-center">
                  {!showMetric
                    ? item.frame_height?.measurement
                      ? (parseFloat(item.frame_height.measurement.replace(/[^\d.]/g, '')) / 25.4).toFixed(2) + '"' // Convert to in
                      : 'N/A'
                    : item.frame_height?.measurement || 'N/A'}
                </td>
                <td className="px-2 py-7 body-sm lg:px-6 text-center">
                  {!showMetric
                    ? item.nose_opening?.measurement
                      ? (parseFloat(item.nose_opening.measurement.replace(/[^\d.]/g, '')) / 25.4).toFixed(2) + '"' // Convert to in
                      : 'N/A'
                    : item.nose_opening?.measurement || 'N/A'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  } else {
    const trueFitActive = (label: string) => {
      return `
      h-4 w-4 rounded-full
      ${
        label === apparelFit
          ? 'h-5 w-5 bg-gradient-radial from-white via-gray-300 to-white shadow-[inset_0px_8.63px_17.25px_0px_rgba(250,250,250,1),inset_0px_1.73px_3.45px_0px_rgba(255,255,255,0.25),inset_0px_-0.86px_3.45px_0px_rgba(171,171,171,0.25),inset_0px_3px_10px_0px_rgba(0,0,0,0.5),0px_3px_4px_0px_rgba(0,0,0,0.15)]'
          : 'bg-grey-11'
      }
    `
    }
    return (
      <div>
        <div className="flex flex-wrap gap-6 px-2 pb-4 lg:px-6 lg:gap-0">
          <h3 className="w-full font-appleGaramond text-24 leading-33 font-normal lg:w-10/12">{category}</h3>
        </div>
        <div className="flex flex-col items-center space-y-2">
          <div className="w-full grid grid-cols-3 text-center body-sm text-black">
            <span className="xl:text-left xl:px-5">{window.DEPT.Strings.product.runsSmall}</span>
            <span>{window.DEPT.Strings.product.trueToSize}</span>
            <span className="xl:text-right xl:px-5">{window.DEPT.Strings.product.runsLarge}</span>
          </div>
        </div>
        <div className="flex flex-col items-center space-y-2 px-2 pb-10 lg:px-5">
          <div className="w-full flex h-6 bg-grey-12 rounded-full">
            <div className="w-full grid grid-cols-5 items-center relative">
              {['Runs Small', 'Runs Slightly Small', 'True to Size', 'Runs Slightly Large', 'Runs Large'].map(
                (size) => (
                  <div key={size} className="relative flex justify-center">
                    <div aria-label={size} className={trueFitActive(size)}></div>
                  </div>
                ),
              )}
            </div>
          </div>
        </div>
        <table className="min-w-full text-left">
          <thead>
            <tr className="border-b border-grey-10">
              <th className="px-2 py-4 body-sm font-semibold lg:px-6">Name</th>
              {columns?.map((item, index) => (
                <th key={index} className="px-2 py-4 body-sm font-semibold lg:px-6">
                  {item}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, rowIndex) => (
              <tr key={`row-${rowIndex}`} className="border-y border-grey-10">
                <td className="px-2 py-7 body-sm font-semibold lg:px-6">{item.size}</td>
                {Object.entries(item)
                  .filter(([key]) => key !== 'size') // Exclude "size" key
                  .map(([, value], colIndex) => {
                    const measurement = parseFloat(
                      (value as { measurement: string }).measurement.replace(/[^\d.]/g, ''),
                    )
                    return (
                      <td key={colIndex} className="px-2 py-7 body-sm lg:px-6">
                        {showMetric
                          ? measurement
                            ? (measurement * 25.4).toFixed() // Convert to mm
                            : 'N/A'
                          : (value as { measurement: string }).measurement || 'N/A'}
                      </td>
                    )
                  })}
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex flex-wrap w-full gap-6 px-2 py-6 lg:px-6 lg:gap-0">
          <div className="w-full flex gap-6 lg:gap-0 lg:grid lg:grid-cols-2 lg:w-5/12">
            <button onClick={() => setShowMetric(false)} className="flex items-center">
              <span className={showMetric ? 'hidden' : 'block'}>
                <Icon name="icon-radio-toggle-on" />
              </span>
              <span className={showMetric ? 'block' : 'hidden'}>
                <Icon name="icon-radio-toggle-off" />
              </span>
              IN.
            </button>
            <button onClick={() => setShowMetric(true)} className="flex items-center">
              <span className={showMetric ? 'block' : 'hidden'}>
                <Icon name="icon-radio-toggle-on" />
              </span>
              <span className={showMetric ? 'hidden' : 'block'}>
                <Icon name="icon-radio-toggle-off" />
              </span>
              MM.
            </button>
          </div>
        </div>
      </div>
    )
  }
}

export default SizeGuideTable
